import { useState, useEffect, useCallback } from "react";
import { Radio, LifeBuoy, ArrowLeft, Send, Plus } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

export const SupportTab = ({ authToken }) => {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);
  const [newTicketForm, setNewTicketForm] = useState({ subject: '', category: 'general', priority: 'medium', message: '' });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  const fetchTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tickets`, { credentials: 'omit', headers: headers() });
      if (res.ok) { setTickets((await res.json()).tickets || []); }
    } catch (err) { console.error('Failed to fetch tickets:', err); }
    finally { setTicketsLoading(false); }
  }, [headers]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tickets/${ticketId}`, { credentials: 'omit', headers: headers() });
      if (res.ok) setViewingTicket(await res.json());
    } catch (err) { console.error('Failed to fetch ticket:', err); }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicketForm.subject || !newTicketForm.message) return;
    setSubmittingTicket(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tickets`, {
        method: 'POST', credentials: 'omit', headers: headers(),
        body: JSON.stringify(newTicketForm)
      });
      if (res.ok) {
        const data = await res.json();
        setTickets(prev => [data, ...prev]);
        setShowNewTicket(false);
        setNewTicketForm({ subject: '', category: 'general', priority: 'medium', message: '' });
      }
    } catch (err) { console.error('Failed to create ticket:', err); }
    finally { setSubmittingTicket(false); }
  };

  const handleReplyTicket = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !viewingTicket) return;
    setSubmittingReply(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tickets/${viewingTicket.ticket_id}/reply`, {
        method: 'POST', credentials: 'omit', headers: headers(),
        body: JSON.stringify({ message: replyMessage })
      });
      if (res.ok) {
        setReplyMessage('');
        fetchTicketDetail(viewingTicket.ticket_id);
        fetchTickets();
      }
    } catch (err) { console.error('Failed to reply:', err); }
    finally { setSubmittingReply(false); }
  };

  return (
  <div className="space-y-6" data-testid="support-tab">
    {viewingTicket ? (
      <div>
        <button onClick={() => { setViewingTicket(null); setReplyMessage(''); }} className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition" data-testid="back-to-tickets"><ArrowLeft className="w-4 h-4" /> Back to Tickets</button>
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white" data-testid="ticket-subject">{viewingTicket.subject}</h3>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${viewingTicket.status === 'open' ? 'bg-blue-500/20 text-blue-400' : viewingTicket.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' : viewingTicket.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`} data-testid="ticket-status">{viewingTicket.status.replace('_', ' ')}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${viewingTicket.priority === 'urgent' ? 'bg-red-500/20 text-red-400' : viewingTicket.priority === 'high' ? 'bg-orange-500/20 text-orange-400' : viewingTicket.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`} data-testid="ticket-priority">{viewingTicket.priority}</span>
                  <span className="text-white/40 text-xs">{viewingTicket.category.replace('_', ' ')}</span>
                  <span className="text-white/30 text-xs">#{viewingTicket.ticket_id}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto" data-testid="ticket-messages">
            {viewingTicket.messages?.map((msg, i) => (
              <div key={i} className={`p-4 rounded-xl ${msg.from_admin ? 'bg-purple-500/10 border border-purple-500/20 ml-4' : 'bg-white/5 border border-white/10 mr-4'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium ${msg.from_admin ? 'text-purple-400' : 'text-white/80'}`}>{msg.from_admin ? 'Support Team' : 'You'}</span>
                  <span className="text-white/20 text-xs">{new Date(msg.timestamp).toLocaleString()}</span>
                </div>
                <p className="text-white/70 text-sm">{msg.message}</p>
              </div>
            ))}
          </div>
          <form onSubmit={handleReplyTicket} className="p-4 border-t border-white/10 flex gap-3">
            <input value={replyMessage} onChange={e => setReplyMessage(e.target.value)} placeholder="Type your reply..." className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50" data-testid="ticket-reply-input" />
            <button type="submit" disabled={submittingReply || !replyMessage.trim()} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition flex items-center gap-2 text-sm" data-testid="ticket-reply-submit"><Send className="w-4 h-4" />{submittingReply ? 'Sending...' : 'Reply'}</button>
          </form>
        </div>
      </div>
    ) : (
      <>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-purple-400" /> Support Tickets</h3>
          <button onClick={() => setShowNewTicket(!showNewTicket)} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2 rounded-xl transition text-sm flex items-center gap-2" data-testid="new-ticket-btn"><Plus className="w-4 h-4" /> New Ticket</button>
        </div>
        {showNewTicket && (
          <form onSubmit={handleCreateTicket} className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <input value={newTicketForm.subject} onChange={e => setNewTicketForm(p => ({ ...p, subject: e.target.value }))} placeholder="Subject" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50" data-testid="new-ticket-subject" />
            <div className="grid grid-cols-2 gap-4">
              <select value={newTicketForm.category} onChange={e => setNewTicketForm(p => ({ ...p, category: e.target.value }))} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none [&>option]:bg-zinc-800" data-testid="new-ticket-category">
                <option value="general">General</option><option value="technical">Technical</option><option value="billing">Billing</option><option value="feature_request">Feature Request</option>
              </select>
              <select value={newTicketForm.priority} onChange={e => setNewTicketForm(p => ({ ...p, priority: e.target.value }))} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none [&>option]:bg-zinc-800" data-testid="new-ticket-priority">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <textarea value={newTicketForm.message} onChange={e => setNewTicketForm(p => ({ ...p, message: e.target.value }))} placeholder="Describe your issue..." rows={4} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50 resize-none" data-testid="new-ticket-message" />
            <button type="submit" disabled={submittingTicket} className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition text-sm" data-testid="submit-ticket-btn">{submittingTicket ? 'Submitting...' : 'Submit Ticket'}</button>
          </form>
        )}
        {ticketsLoading ? (
          <div className="text-center py-12"><Radio className="w-8 h-8 text-purple-400 animate-pulse mx-auto" /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 bg-white/5 border border-white/10 rounded-2xl"><LifeBuoy className="w-12 h-12 text-white/15 mx-auto mb-4" /><h4 className="text-white font-medium mb-2">No Tickets Yet</h4><p className="text-white/40 text-sm">Need help? Create a support ticket and we'll respond within 24 hours.</p></div>
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((ticket, i) => (
              <button key={ticket.ticket_id || i} onClick={() => fetchTicketDetail(ticket.ticket_id)} className="w-full text-left bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/[0.07] transition" data-testid={`ticket-item-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium text-sm">{ticket.subject}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ticket.status === 'open' ? 'bg-blue-500/20 text-blue-400' : ticket.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' : ticket.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{ticket.status?.replace('_', ' ')}</span>
                      <span className="text-white/30 text-xs">{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </>
    )}
  </div>
  );
};
